import React from "react";
import { Link } from "@tanstack/react-router";
import { Award } from "lucide-react";
import type {
  MembershipPlan,
  OfferBenefitSelect,
  OfferSelect,
  OfferTagSelect,
} from "../../drizzle/schema";
import { Badge } from "./ui/badge";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { MarkdownContent } from "./markdown-content";
import { Feature, hasFeature } from "~/lib/has-feature";
import { PriceDisplay } from "./ui/price-display";
import {
  shouldShowBookingCTAs,
  shouldShowMessagingCTAs,
  getCoachCTAText,
  type MinimalCoachData,
} from "~/lib/coach-cta-utils";
type Offer = OfferSelect & {
  tags: OfferTagSelect[];
  benefits: OfferBenefitSelect[];
};

export const OfferCard = ({
  offer,
  coach,
  createCheckout,
}: {
  offer: Offer;
  coach: MinimalCoachData & { membershipPlan: MembershipPlan };
  createCheckout?: {
    mutate: (data: { offerId: string }) => void;
    isPending?: boolean;
    error?: string | null;
  };
}) => {
  const hasContent =
    0 < offer.tags.length ||
    offer.headline ||
    offer.description ||
    0 < offer.benefits.length;

  if (!hasContent) {
    return null;
  }

  const handleBuyNow = () => {
    if (createCheckout) {
      createCheckout.mutate({ offerId: offer.id });
    }
  };

  return (
    <Card>
      <CardContent>
        <div className="mt-8 flex flex-col items-center gap-8">
          {0 < offer.tags.length && (
            <div className="flex items-center justify-center gap-2">
              {offer.tags.map((t) => (
                <Badge key={t.id} variant="outline-secondary">
                  {t.title}
                </Badge>
              ))}
            </div>
          )}
          <div className="flex flex-col items-center gap-4">
            {offer.headline && (
              <h2 className="text-text-primary font-heading text-center text-4xl leading-none">
                {offer.headline}
              </h2>
            )}
            {offer.description && (
              <div className="text-text-secondary text-center text-lg">
                <MarkdownContent content={offer.description} />
              </div>
            )}
          </div>
          {0 < offer.benefits.length && (
            <div className="text-text-secondary flex flex-col flex-wrap items-center justify-center gap-4 text-sm sm:flex-row">
              {offer.benefits.map((benefit) => (
                <div key={benefit.id} className="flex items-center gap-2">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-gray-100 text-purple-700">
                    <Award />
                  </div>
                  {benefit.title}
                </div>
              ))}
            </div>
          )}
          {hasFeature(coach, Feature.OfferPage) ? (
            <Link
              to="/$username/offer/$offerId"
              params={{ username: coach.username, offerId: offer.id }}
            >
              <Button variant="fancy" size="lg">
                Read more
              </Button>
            </Link>
          ) : offer.directSaleEnabled ? (
            <>
              <div className="flex flex-col items-center gap-2">
                <PriceDisplay
                  price={offer.price!}
                  currency={offer.currency!}
                  originalPrice={offer.originalPrice}
                  size="lg"
                />
                {/* Show error message if checkout creation failed */}
                {createCheckout?.error && (
                  <div className="text-sm text-red-600">
                    {createCheckout.error}
                  </div>
                )}
                <Button
                  onClick={handleBuyNow}
                  variant="fancy"
                  size="lg"
                  disabled={createCheckout?.isPending}
                >
                  Buy Now
                </Button>
              </div>
            </>
          ) : shouldShowMessagingCTAs(coach) ? (
            <Link to="/$username/contact" params={{ username: coach.username }}>
              <Button variant="fancy" size="lg">
                {getCoachCTAText(coach)}
              </Button>
            </Link>
          ) : (
            shouldShowBookingCTAs(coach) && (
              <Link to="/$username/book" params={{ username: coach.username }}>
                <Button variant="fancy" size="lg">
                  {getCoachCTAText(coach)}
                </Button>
              </Link>
            )
          )}
        </div>
      </CardContent>
    </Card>
  );
};
